'use client'

import Hls from 'hls.js'
import { generateVideoPosterSrc, VIDEO_POSTER_SIZE } from "@v2/components/MediaV2";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Paper,
    Stack,
    IconButton,
    Typography,
    Slider,
    Tooltip,
    CircularProgress,
    useMediaQuery,
} from '@mui/material'

import {
    Gauge as GaugeIcon,
    Maximize as MaximizeIcon,
    Minimize as MinimizeIcon,
    Pause as PauseIcon,
    PictureInPicture as PictureInPictureIcon,
    PictureInPicture2 as PictureInPicture2Icon,
    Play as PlayIcon,
    RotateCcw as RotateCcwIcon,
    RotateCw as RotateCwIcon,
    Settings as SettingsIcon,
    Volume2 as Volume2Icon,
    VolumeX as VolumeXIcon,
} from 'lucide-react'
import CenterBox from '@v2/components/styled/CenterBox'
import { VideoType } from '@/app/types/video'
import useVideoView from '@/app/hooks/useVideoView'


type Props = {
    data: VideoType
    embed?: boolean
    // An image media ID that stands in for the video's own poster. A section that frames
    // the player differently -- 16:9 next to a checklist, say -- can hand it a cover cut
    // for that frame without touching the record.
    posterId?: string | null
    startAtSeconds?: number
    onReady?: () => void
    onPlayingChange?: (playing: boolean) => void
    onSeekingChange?: (seeking: boolean) => void
    // Fires whenever the poster cover the player draws at position zero comes or goes,
    // so a section can keep its own cover decoration in step with it.
    onStartCoverChange?: (visible: boolean) => void
}

// Fixed size so the poster URL is identical on server and client. A viewport
// dependent poster is empty during SSR, which hides the thumbnail from crawlers
// and costs a second fetch after hydration. Shared with VideoStream, which draws the
// same poster while this player loads.
const POSTER_SIZE = VIDEO_POSTER_SIZE

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n))
}

function formatTime(totalSeconds: number) {
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) totalSeconds = 0
    const s = Math.floor(totalSeconds % 60)
    const m = Math.floor((totalSeconds / 60) % 60)
    const h = Math.floor(totalSeconds / 3600)

    const pad = (x: number) => String(x).padStart(2, '0')
    if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
    return `${m}:${pad(s)}`
}

function getBufferedEnd(video: HTMLVideoElement, currentTime: number) {
    const ranges = video.buffered
    if (!ranges || ranges.length === 0) return 0

    for (let i = 0; i < ranges.length; i += 1) {
        const start = ranges.start(i)
        const end = ranges.end(i)
        if (currentTime >= start && currentTime <= end) return end
    }

    return ranges.end(ranges.length - 1)
}

function buildUrl(base: string, pathOrUrl?: string) {
    if (!pathOrUrl) return ''
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
    const baseClean = base.replace(/\/+$/, '')
    const pathClean = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
    return `${baseClean}${pathClean}`
}

function withQueryParams(pathOrUrl: string, params: Record<string, string | number | undefined>) {
    try {
        const isAbsolute = /^https?:\/\//i.test(pathOrUrl)
        const url = new URL(pathOrUrl, 'http://local')
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined) return
            url.searchParams.set(key, String(value))
        })

        if (isAbsolute) return url.toString()
        return `${url.pathname}${url.search}${url.hash}`
    } catch {
        const searchParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined) return
            searchParams.set(key, String(value))
        })
        const query = searchParams.toString()
        if (!query) return pathOrUrl
        return `${pathOrUrl}${pathOrUrl.includes('?') ? '&' : '?'}${query}`
    }
}

export default function CustomHlsMuiPlayer({ data, embed = false, posterId, startAtSeconds, onReady, onPlayingChange, onSeekingChange, onStartCoverChange }: Props) {
    const BASE_API = process.env.NEXT_PUBLIC_MEDIA_URL || ''
    const { sendView } = useVideoView(data.id)

    const containerRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const hlsRef = useRef<Hls | null>(null)
    const hasStartedLoadRef = useRef(false)
    const hasPrimedFirstSegmentRef = useRef(false)
    const hasNotifiedReadyRef = useRef(false)
    const initialResumeTimeRef = useRef(0)
    const attachedRef = useRef(false)
    const attachSourceRef = useRef<(() => void) | null>(null)

    const resumeKey = useMemo(() => `video:resume:${data.id}`, [data.id])
    const persistResume = useCallback(
        (time: number) => {
            if (time <= 0) {
                localStorage.removeItem(resumeKey)
            } else {
                localStorage.setItem(resumeKey, String(time))
            }
        },
        [resumeKey],
    )

    const [isNativeHls, setIsNativeHls] = useState(false)
    const [isReady, setIsReady] = useState(false)
    const [isBuffering, setIsBuffering] = useState(false)
    // The media source is attached on first user intent, not on mount, so the
    // element keeps a crawlable <source> until someone actually engages with it.
    const [attached, setAttached] = useState(false)

    const [paused, setPaused] = useState(true)
    const [muted, setMuted] = useState(false)
    const [volume, setVolume] = useState(1)

    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [bufferedUntil, setBufferedUntil] = useState(0)
    const [seeking, setSeeking] = useState(false)
    const [manualSeeking, setManualSeeking] = useState(false)
    // Dropping the seek handle ends the seek at once, but play() only resolves a frame
    // or two later. In between the element is still paused, and if the handle landed on
    // zero the start cover would flash for those frames. This keeps it down until
    // playback has actually resumed.
    const [resuming, setResuming] = useState(false)
    const [seekValue, setSeekValue] = useState(0)

    const [isFullscreen, setIsFullscreen] = useState(false)
    const isDraggingRef = useRef(false)
    const isSeekingRef = useRef(false)
    const manualSeekingRef = useRef(false)
    const wasPlayingRef = useRef(false)
    const [actionIcon, setActionIcon] = useState<'play' | 'pause' | null>(null)
    const [actionKey, setActionKey] = useState(0)
    const [pipEnabled, setPipEnabled] = useState(false)
    const [isPip, setIsPip] = useState(false)
    const [centerActionVisible, setCenterActionVisible] = useState(true)

    const [controlsVisible, setControlsVisible] = useState(true)
    const controlsVisibleRef = useRef(true)
    const hideTimerRef = useRef<number | null>(null)
    const pausedRef = useRef(true)
    const centerActionTimerRef = useRef<number | null>(null)
    const fullscreenScrollPositionRef = useRef<{ x: number; y: number } | null>(null)
    const wasFullscreenRef = useRef(false)

    const [currentLevel, setCurrentLevel] = useState(-1) // -1 = auto
    const [isAutoLevel, setIsAutoLevel] = useState(true)
    const requestedLevelRef = useRef<number | null>(null)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [qualityHover, setQualityHover] = useState(false)
    const [speedHover, setSpeedHover] = useState(false)
    const menusRef = useRef<HTMLDivElement>(null)
    const qualityMenuOpenRef = useRef(false)
    const speedMenuOpenRef = useRef(false)
    const blockVideoClickRef = useRef(false)
    const blockVideoClickTimerRef = useRef<number | null>(null)
    const swallowNextVideoClickRef = useRef(false)
    const isTouchDevice = useMediaQuery('(pointer: coarse)')
    const isTouchDeviceRef = useRef(isTouchDevice)
    const touchStartRef = useRef<{ x: number; y: number } | null>(null)
    const swipeActiveRef = useRef(false)
    const swipeDeltaRef = useRef(0)
    const touchStartTimeRef = useRef(0)
    const qualities = data.media.video?.meta?.qualities ?? []
    const src = buildUrl(BASE_API, data.media.video?.master_playlist_path)
    const posterMediaId = posterId || data.poster?.id
    // Built through the shared helper rather than by hand: VideoStream renders the same
    // poster over this one, and a query that differs by even an `action` downloads it twice.
    const poster = posterMediaId
        ? generateVideoPosterSrc(posterMediaId)
        : buildUrl(BASE_API, withQueryParams(`/api/v1/media/${data.media.id}?request_poster=1`, POSTER_SIZE))
    // Declares the stream on the element itself so crawlers find a fetchable video
    // URL without running any JS. It must stay identical to the contentUrl in the
    // page's JSON-LD, otherwise Google reports conflicting video URLs. Browsers
    // never load it: hls.js swaps in an MSE blob, and Safari sets the same URL
    // directly, both on first user intent. The original upload is deliberately not
    // used here, it reaches 700MB.

    const notifyReady = useCallback(() => {
        if (hasNotifiedReadyRef.current) return
        hasNotifiedReadyRef.current = true
        onReady?.()
    }, [onReady])

    const showControls = useCallback((opts?: { swallowNextClick?: boolean }) => {
        const wasVisible = controlsVisibleRef.current
        setControlsVisible(true)
        controlsVisibleRef.current = true
        if (!wasVisible && opts?.swallowNextClick) swallowNextVideoClickRef.current = true
        if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = window.setTimeout(() => {
            if (pausedRef.current || manualSeekingRef.current) return
            setControlsVisible(false)
            controlsVisibleRef.current = false
        }, 2500)
    }, [])
    const hideControls = useCallback(() => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current)
            hideTimerRef.current = null
        }
        setControlsVisible(false)
        controlsVisibleRef.current = false
    }, [])
    const onMouseMoveControls = useCallback(() => showControls(), [showControls])
    const onTouchStartControls = useCallback(() => {
        if (!isTouchDevice) {
            showControls({ swallowNextClick: true })
            return
        }
        if (!controlsVisibleRef.current) showControls({ swallowNextClick: true })
    }, [isTouchDevice, showControls])
    const showCenterActionTemporarily = useCallback((ms = 3000) => {
        setCenterActionVisible(true)
        if (centerActionTimerRef.current) window.clearTimeout(centerActionTimerRef.current)
        centerActionTimerRef.current = window.setTimeout(() => {
            if (!pausedRef.current) setCenterActionVisible(false)
        }, ms)
    }, [])
    const showCenterActionPermanently = useCallback(() => {
        if (centerActionTimerRef.current) {
            window.clearTimeout(centerActionTimerRef.current)
            centerActionTimerRef.current = null
        }
        setCenterActionVisible(true)
    }, [])
    const restoreFullscreenScroll = useCallback(() => {
        const position = fullscreenScrollPositionRef.current
        if (!position) return
        fullscreenScrollPositionRef.current = null
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.scrollTo({ left: position.x, top: position.y, behavior: 'auto' })
            })
        })
    }, [])
    useEffect(() => {
        isTouchDeviceRef.current = isTouchDevice
    }, [isTouchDevice])
    useEffect(() => {
        isSeekingRef.current = seeking
    }, [seeking])
    useEffect(() => {
        manualSeekingRef.current = manualSeeking
    }, [manualSeeking])

    // Init player (HLS + listeners)
    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        if (!src) return

        hasStartedLoadRef.current = false
        hasPrimedFirstSegmentRef.current = false
        hasNotifiedReadyRef.current = false
        attachedRef.current = false
        setAttached(false)
        setIsReady(false)
        setIsBuffering(false)

        // native hls detection
        const native = video.canPlayType('application/vnd.apple.mpegurl') === 'probably'
        setIsNativeHls(native)

        // reset any previous hls instance
        if (hlsRef.current) {
            hlsRef.current.destroy()
            hlsRef.current = null
        }

        // Resume
        const saved = localStorage.getItem(resumeKey)
        const parsedSavedTime = saved ? Number(saved) : 0
        const savedTime = Number.isFinite(parsedSavedTime) ? Math.max(0, parsedSavedTime) : 0
        const hasExplicitStartTime =
            typeof startAtSeconds === 'number' &&
            Number.isFinite(startAtSeconds) &&
            startAtSeconds >= 0
        const forcedStartTime = hasExplicitStartTime ? startAtSeconds : 0
        const initialStartTime = hasExplicitStartTime ? forcedStartTime : savedTime
        initialResumeTimeRef.current = initialStartTime

        const applyInitialResume = () => {
            const resumeTime = initialResumeTimeRef.current
            if (!(resumeTime > 0)) return

            const maxTime = Math.max((video.duration || data.media.video.duration || 0) - 0.2, 0)
            const target = clamp(resumeTime, 0, maxTime)
            video.currentTime = target
            setCurrentTime(target)
            setSeekValue(target)

            // اگر seek واقعاً اعمال شد، دوباره تکرارش نکنیم
            if (Math.abs(video.currentTime - target) < 0.5 || video.currentTime > 0.1) {
                initialResumeTimeRef.current = 0
            }
        }

        const onLoadedMetadata = () => {
            setDuration(video.duration || data.media.video.duration || 0)
            setBufferedUntil(getBufferedEnd(video, video.currentTime))
            applyInitialResume()
        }

        const onTimeUpdate = () => {
            setCurrentTime(video.currentTime)
            setBufferedUntil(getBufferedEnd(video, video.currentTime))
            if (!seeking) setSeekValue(video.currentTime)
            persistResume(video.currentTime)
            if (video.currentTime > 0.1 && initialResumeTimeRef.current > 0) {
                initialResumeTimeRef.current = 0
            }
        }

        const onPlay = () => {
            sendView()
            if (!isTouchDeviceRef.current) {
                setActionIcon('play')
                setActionKey((k) => k + 1)
            }
            showCenterActionTemporarily(3000)
        }
        const onPause = () => {
            setPaused(true)
            pausedRef.current = true
            onPlayingChange?.(false)
            if (hlsRef.current && !manualSeekingRef.current) hlsRef.current.stopLoad()
            if (!isTouchDeviceRef.current) {
                setActionIcon('pause')
                setActionKey((k) => k + 1)
            }
            showCenterActionPermanently()
        }

        const onSeeking = () => {
            if (!isDraggingRef.current) setSeeking(true)
        }
        const onSeeked = () => {
            if (isDraggingRef.current) return
            setSeeking(false)
            setCurrentTime(video.currentTime)
            setSeekValue(video.currentTime)
            setIsBuffering(false)
        }

        const onWaiting = () => {
            if (isDraggingRef.current || isSeekingRef.current) return
            setIsBuffering(true)
        }
        const onPlaying = () => {
            setPaused(false)
            pausedRef.current = false
            setIsBuffering(false)
            onPlayingChange?.(true)
            notifyReady()
        }
        const onCanPlay = () => {
            applyInitialResume()
            setIsReady(true)
            setIsBuffering(false)
            notifyReady()
        }
        const onProgress = () => {
            setBufferedUntil(getBufferedEnd(video, video.currentTime))
        }
        const onEnded = () => {
            localStorage.removeItem(resumeKey)
            setCurrentTime(0)
            setSeekValue(0)
            setSeeking(false)
        }
        const onEnterPip = () => setIsPip(true)
        const onLeavePip = () => setIsPip(false)

        video.addEventListener('loadedmetadata', onLoadedMetadata)
        video.addEventListener('timeupdate', onTimeUpdate)
        video.addEventListener('play', onPlay)
        video.addEventListener('pause', onPause)
        video.addEventListener('seeking', onSeeking)
        video.addEventListener('seeked', onSeeked)
        video.addEventListener('waiting', onWaiting)
        video.addEventListener('playing', onPlaying)
        video.addEventListener('canplay', onCanPlay)
        video.addEventListener('progress', onProgress)
        video.addEventListener('ended', onEnded)
        video.addEventListener('enterpictureinpicture', onEnterPip)
        video.addEventListener('leavepictureinpicture', onLeavePip)

        // Apply initial volume/mute
        video.volume = volume
        video.muted = muted
        video.playbackRate = playbackRate
        setPipEnabled(Boolean(document.pictureInPictureEnabled && video.requestPictureInPicture))

        // Attaching the source replaces the crawlable <source> with either the HLS
        // manifest or, under hls.js, an unfetchable blob: URL. Google reported those
        // as a conflicting third video URL, so hold off until the user engages.
        const attachSource = () => {
            if (attachedRef.current) return
            attachedRef.current = true
            setAttached(true)
            setIsBuffering(true)

            if (native) {
                // preload="none" is what stops segments from loading before the first
                // play, so setting src here costs nothing until playback starts.
                video.src = src
                if (initialStartTime > 0) {
                    // برای resume باید metadata قبل از play لود شود تا seek از ابتدا اعمال شود.
                    video.load()
                    hasStartedLoadRef.current = true
                }
                // quality menu won't work for native
                return
            }
            if (!Hls.isSupported()) return

            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                autoStartLoad: false,
            })
            hlsRef.current = hls

            hls.loadSource(src)
            hls.attachMedia(video)

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setCurrentLevel(hls.currentLevel)
                setIsReady(true)
                setIsBuffering(false)
                notifyReady()
                // فقط برای آماده‌بودن پخش، بافر اولیه می‌گیریم.
                if (!hasPrimedFirstSegmentRef.current) {
                    hls.startLoad(initialStartTime > 0 ? initialStartTime : -1)
                    hasStartedLoadRef.current = true
                }
            })

            hls.on(Hls.Events.FRAG_BUFFERED, (_e, d) => {
                if (hasPrimedFirstSegmentRef.current) return
                if (d.frag?.type !== 'main') return
                hasPrimedFirstSegmentRef.current = true
                if (video.paused) hls.stopLoad()
            })

            hls.on(Hls.Events.LEVEL_SWITCHED, (_e, d) => {
                setCurrentLevel(d.level)
                if (!isAutoLevel && requestedLevelRef.current !== null && d.level !== requestedLevelRef.current) {
                    hls.currentLevel = d.level
                    hls.nextLevel = requestedLevelRef.current
                }
            })

            hls.on(Hls.Events.ERROR, (_e, _d) => {
                // اگر CORS یا فایل‌ها مشکل داشته باشن معمولاً اینجا هم خطا میاد
                // console.error('HLS error', d)
            })
        }

        attachSourceRef.current = attachSource

        // Real users produce one of these before they can reach the play button;
        // crawlers produce none, so they keep seeing the plain <source>.
        const container = containerRef.current
        const intentEvents = ['pointerdown', 'touchstart', 'mouseenter', 'focusin'] as const
        intentEvents.forEach((event) =>
            container?.addEventListener(event, attachSource, { once: true, passive: true }),
        )

        // The player is interactive and showing its poster at this point. Consumers
        // like VideoStream gate their placeholder on this, and the source now attaches
        // too late to be their signal.
        notifyReady()

        showControls()

        return () => {
            attachSourceRef.current = null
            intentEvents.forEach((event) => container?.removeEventListener(event, attachSource))

            video.removeEventListener('loadedmetadata', onLoadedMetadata)
            video.removeEventListener('timeupdate', onTimeUpdate)
            video.removeEventListener('play', onPlay)
            video.removeEventListener('pause', onPause)
            video.removeEventListener('seeking', onSeeking)
            video.removeEventListener('seeked', onSeeked)
            video.removeEventListener('waiting', onWaiting)
            video.removeEventListener('playing', onPlaying)
            video.removeEventListener('canplay', onCanPlay)
            video.removeEventListener('progress', onProgress)
            video.removeEventListener('ended', onEnded)
            video.removeEventListener('enterpictureinpicture', onEnterPip)
            video.removeEventListener('leavepictureinpicture', onLeavePip)

            if (hlsRef.current) {
                hlsRef.current.destroy()
                hlsRef.current = null
            }
            if (centerActionTimerRef.current) {
                window.clearTimeout(centerActionTimerRef.current)
                centerActionTimerRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifyReady, persistResume, src, startAtSeconds])

    useEffect(() => {
        controlsVisibleRef.current = controlsVisible
    }, [controlsVisible])

    useEffect(() => {
        pausedRef.current = paused
        if (paused) {
            if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current)
                hideTimerRef.current = null
            }
            setControlsVisible(true)
            controlsVisibleRef.current = true
        }
    }, [paused])

    useEffect(() => {
        qualityMenuOpenRef.current = qualityHover
        speedMenuOpenRef.current = speedHover
    }, [qualityHover, speedHover])

    // fullscreen state watcher (standard + iOS Safari)
    useEffect(() => {
        const video = videoRef.current as (HTMLVideoElement & {
            webkitDisplayingFullscreen?: boolean
        }) | null
        const onFs = () => {
            const fullscreen = Boolean(
                document.fullscreenElement ||
                (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement,
            )
            setIsFullscreen(fullscreen)
            if (wasFullscreenRef.current && !fullscreen) restoreFullscreenScroll()
            wasFullscreenRef.current = fullscreen
        }
        const onWebkitBeginFullscreen = () => {
            if (!fullscreenScrollPositionRef.current) {
                fullscreenScrollPositionRef.current = { x: window.scrollX, y: window.scrollY }
            }
            wasFullscreenRef.current = true
            setIsFullscreen(true)
        }
        const onWebkitEndFullscreen = () => {
            wasFullscreenRef.current = false
            setIsFullscreen(false)
            restoreFullscreenScroll()
        }
        document.addEventListener('fullscreenchange', onFs)
        document.addEventListener('webkitfullscreenchange', onFs as EventListener)
        video?.addEventListener('webkitbeginfullscreen', onWebkitBeginFullscreen)
        video?.addEventListener('webkitendfullscreen', onWebkitEndFullscreen)
        return () => {
            document.removeEventListener('fullscreenchange', onFs)
            document.removeEventListener('webkitfullscreenchange', onFs as EventListener)
            video?.removeEventListener('webkitbeginfullscreen', onWebkitBeginFullscreen)
            video?.removeEventListener('webkitendfullscreen', onWebkitEndFullscreen)
        }
    }, [restoreFullscreenScroll])

  useEffect(() => {
    const onOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (!qualityMenuOpenRef.current && !speedMenuOpenRef.current) return
      const target = e.target as Node | null
      if (menusRef.current && target && menusRef.current.contains(target)) return
      setQualityHover(false)
      setSpeedHover(false)
      qualityMenuOpenRef.current = false
      speedMenuOpenRef.current = false
      if (target && target === videoRef.current) {
        blockVideoClickRef.current = true
      }
    }

    document.addEventListener('mousedown', onOutsideClick)
    document.addEventListener('touchstart', onOutsideClick)
    return () => {
      document.removeEventListener('mousedown', onOutsideClick)
      document.removeEventListener('touchstart', onOutsideClick)
    }
  }, [qualityHover, speedHover])

  useEffect(() => {
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (containerRef.current && target && containerRef.current.contains(target)) return
      if (pausedRef.current) return
      hideControls()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('touchstart', onDocClick)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('touchstart', onDocClick)
    }
  }, [])

    // Keyboard shortcuts (روی container وقتی فوکوس داره یا موس روشه)
    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const onKey = (e: KeyboardEvent) => {
            const video = videoRef.current
            if (!video) return

            if (e.code === 'Space') {
                e.preventDefault()
                togglePlay()
            } else if (e.code === 'ArrowLeft') {
                e.preventDefault()
                video.currentTime = clamp(video.currentTime - 1, 0, duration || video.duration || 0)
                persistResume(video.currentTime)
            } else if (e.code === 'ArrowRight') {
                e.preventDefault()
                video.currentTime = clamp(video.currentTime + 1, 0, duration || video.duration || video.currentTime + 1)
                persistResume(video.currentTime)
            } else if (e.code === 'KeyF') {
                e.preventDefault()
                toggleFullscreen()
            } else if (e.code === 'ArrowUp') {
                e.preventDefault()
                setPlayerVolume(clamp(volume + 0.05, 0, 1))
            } else if (e.code === 'ArrowDown') {
                e.preventDefault()
                setPlayerVolume(clamp(volume - 0.05, 0, 1))
            } else if (e.code === 'KeyM') {
                e.preventDefault()
                toggleMute()
            }
        }

        el.addEventListener('keydown', onKey)
        return () => el.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration, muted, persistResume, volume])

    const togglePlay = useCallback(async () => {
        const video = videoRef.current
        if (!video) return
        showControls()

        try {
            if (video.paused) {
                if (video.ended) {
                    video.currentTime = 0
                }
                // Normally an intent event already attached the source; this covers
                // any path that reaches play without one.
                attachSourceRef.current?.()
                if (!hasStartedLoadRef.current) {
                    const native = video.canPlayType('application/vnd.apple.mpegurl') === 'probably'
                    if (native) {
                        video.src = src
                        video.load()
                    } else if (hlsRef.current) {
                        hlsRef.current.startLoad(initialResumeTimeRef.current > 0 ? initialResumeTimeRef.current : -1)
                    }
                    hasStartedLoadRef.current = true
                } else if (hlsRef.current) {
                    hlsRef.current.startLoad(video.ended ? 0 : -1)
                }
                await video.play()
            }
            else video.pause()
        } catch {
            // autoplay restriction etc.
        }
    }, [showControls, src])

    const onVideoClick = useCallback(() => {
        if (swallowNextVideoClickRef.current) {
            swallowNextVideoClickRef.current = false
            if (isTouchDevice) {
                if (pausedRef.current) showCenterActionPermanently()
                else showCenterActionTemporarily(3000)
            }
            return
        }
        if (isTouchDevice) {
            if (pausedRef.current) {
                showControls()
                showCenterActionPermanently()
                return
            }
            if (controlsVisibleRef.current) hideControls()
            else showControls()
            if (!pausedRef.current) showCenterActionTemporarily(3000)
            return
        }
        if (blockVideoClickRef.current) {
            blockVideoClickRef.current = false
            return
        }
        if (qualityMenuOpenRef.current || speedMenuOpenRef.current) {
            setQualityHover(false)
            setSpeedHover(false)
            qualityMenuOpenRef.current = false
            speedMenuOpenRef.current = false
            return
        }
        if (!controlsVisibleRef.current) {
            showControls()
        }
        togglePlay()
    }, [hideControls, isTouchDevice, showCenterActionPermanently, showCenterActionTemporarily, showControls, togglePlay])

    const armBlockVideoClick = useCallback(() => {
        blockVideoClickRef.current = true
        if (blockVideoClickTimerRef.current) {
            window.clearTimeout(blockVideoClickTimerRef.current)
        }
        blockVideoClickTimerRef.current = window.setTimeout(() => {
            blockVideoClickRef.current = false
        }, 300)
    }, [])

    const seekTo = useCallback(
        (t: number) => {
            const video = videoRef.current
            if (!video) return
            const next = clamp(t, 0, (video.duration || duration || 0) || 0)
            video.currentTime = next
            setCurrentTime(next)
            setSeekValue(next)
            persistResume(next)
        },
        [duration, persistResume],
    )

    const onVideoTouchStart = useCallback(
        (e: React.TouchEvent<HTMLVideoElement>) => {
            if (!isTouchDevice) return
            const touch = e.touches[0]
            if (!touch) return
            touchStartRef.current = { x: touch.clientX, y: touch.clientY }
            swipeActiveRef.current = false
            swipeDeltaRef.current = 0
            touchStartTimeRef.current = videoRef.current?.currentTime ?? currentTime
        },
        [currentTime, isTouchDevice],
    )

    const onVideoTouchMove = useCallback(
        (e: React.TouchEvent<HTMLVideoElement>) => {
            if (!isTouchDevice) return
            const start = touchStartRef.current
            const touch = e.touches[0]
            if (!start || !touch) return
            const dx = touch.clientX - start.x
            const dy = touch.clientY - start.y

            if (!swipeActiveRef.current) {
                if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return
                if (Math.abs(dy) > Math.abs(dx)) return
                swipeActiveRef.current = true
                swallowNextVideoClickRef.current = true
            }

            if (!swipeActiveRef.current) return
            e.preventDefault()
            const seconds = dx / 30
            const video = videoRef.current
            const max = video?.duration || duration || 0
            const rawNext = clamp(touchStartTimeRef.current + seconds, 0, max)
            const step = 1 / 60
            const next = Math.round(rawNext / step) * step
            swipeDeltaRef.current = next - touchStartTimeRef.current
            if (!isDraggingRef.current) {
                isDraggingRef.current = true
                setSeeking(true)
                setManualSeeking(true)
                manualSeekingRef.current = true
                onSeekingChange?.(true)
                showControls()
            }
            setIsBuffering(false)
            setSeekValue(next)
            setCurrentTime(next)
            if (video) video.currentTime = next
        },
        [duration, isTouchDevice, onSeekingChange, showControls],
    )

    const onVideoTouchEnd = useCallback(() => {
        if (!isTouchDevice) return
        if (swipeActiveRef.current) {
            if (isDraggingRef.current) {
                isDraggingRef.current = false
                setSeeking(false)
                setManualSeeking(false)
                manualSeekingRef.current = false
                onSeekingChange?.(false)
                seekTo(touchStartTimeRef.current + swipeDeltaRef.current)
                showControls()
            }
            swallowNextVideoClickRef.current = false
            armBlockVideoClick()
        }
        touchStartRef.current = null
        swipeActiveRef.current = false
        swipeDeltaRef.current = 0
    }, [armBlockVideoClick, isTouchDevice, onSeekingChange, seekTo, showControls])

    const jump = useCallback(
        (delta: number) => {
            const video = videoRef.current
            if (!video) return
            showControls()
            seekTo((video.currentTime || 0) + delta)
        },
        [seekTo, showControls],
    )

    const setPlayerVolume = useCallback(
        (v: number) => {
            const video = videoRef.current
            if (!video) return
            const vv = clamp(v, 0, 1)
            setVolume(vv)
            video.volume = vv
            if (vv === 0) {
                setMuted(true)
                video.muted = true
            } else if (muted) {
                setMuted(false)
                video.muted = false
            }
        },
        [muted],
    )

    const toggleMute = useCallback(() => {
        const video = videoRef.current
        if (!video) return
        showControls()
        const next = !video.muted
        video.muted = next
        setMuted(next)
    }, [showControls])

    const toggleFullscreen = useCallback(async () => {
        const el = containerRef.current
        if (!el) return
        showControls()

        const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void }
        const isFs = Boolean(doc.fullscreenElement || doc.webkitFullscreenElement)
        if (!isFs) {
            fullscreenScrollPositionRef.current = { x: window.scrollX, y: window.scrollY }
            const video = videoRef.current as HTMLVideoElement & { webkitEnterFullscreen?: () => void }
            if (el.requestFullscreen) {
                try {
                    await el.requestFullscreen()
                } catch {
                    video?.webkitEnterFullscreen?.()
                }
            } else {
                video?.webkitEnterFullscreen?.()
            }
        } else if (doc.fullscreenElement && document.exitFullscreen) {
            await document.exitFullscreen()
        } else if (doc.webkitFullscreenElement) {
            doc.webkitExitFullscreen?.()
        }
    }, [showControls])

    const onQualitySelect = useCallback((levelIndex: number) => {
        // فقط وقتی hls.js داریم (نه native)
        if (!hlsRef.current) {
            setQualityHover(false)
            return
        }
        if (levelIndex === -1) {
            hlsRef.current.nextLevel = -1
            hlsRef.current.loadLevel = -1
            setIsAutoLevel(true)
            requestedLevelRef.current = -1
        } else {
            hlsRef.current.nextLevel = levelIndex
            setIsAutoLevel(false)
            requestedLevelRef.current = levelIndex
        }
        if (levelIndex !== -1) setCurrentLevel(levelIndex)
        setQualityHover(false)
    }, [])
    const toggleQualityMenu = useCallback(() => {
        setQualityHover((prev) => {
            const next = !prev
            if (next) setSpeedHover(false)
            qualityMenuOpenRef.current = next
            speedMenuOpenRef.current = false
            return next
        })
    }, [])
    const speedOptions = useMemo(() => [0.5, 1, 1.5, 2], [])

    const onSpeedSelect = useCallback((rate: number) => {
        const video = videoRef.current
        if (!video) return
        setPlaybackRate(rate)
        video.playbackRate = rate
        setSpeedHover(false)
    }, [])
    const toggleSpeedMenu = useCallback(() => {
        setSpeedHover((prev) => {
            const next = !prev
            if (next) setQualityHover(false)
            speedMenuOpenRef.current = next
            qualityMenuOpenRef.current = false
            return next
        })
    }, [])

    const togglePip = useCallback(async () => {
        const video = videoRef.current
        if (!video) return
        if (!document.pictureInPictureEnabled || !video.requestPictureInPicture) return
        showControls()

        try {
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture()
            } else {
                await video.requestPictureInPicture()
            }
        } catch {
            // ignore user gesture or browser support errors
        }
    }, [showControls])

    // UI helpers
    const showQuality = !isNativeHls && qualities.length > 0
    const mobileCenterActionShown = (paused || centerActionVisible) && !manualSeeking
    const showStartCover = Boolean(poster) && paused && currentTime <= 0.05 && !seeking && !resuming
    const bufferedPercent = duration > 0 ? clamp((bufferedUntil / duration) * 100, 0, 100) : 0

    useEffect(() => {
        onStartCoverChange?.(showStartCover)
    }, [showStartCover, onStartCoverChange])

    return (
        <Paper
            data-embed={embed ? 'true' : 'false'}
            ref={containerRef}
      tabIndex={0}
      elevation={0}
      onMouseMove={onMouseMoveControls}
      onTouchStart={onTouchStartControls}
      sx={{
                position: 'relative',
                bgcolor: 'black',
                borderRadius: 0,
                overflow: 'hidden',
                outline: 'none',
                userSelect: 'none',
                width: '100%',
                height: embed ? '100%' : 'auto',
                maxHeight: embed ? '100%' : '80vh',
                '&:fullscreen': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100vw',
                },
                '&:fullscreen > div': {
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                '&:fullscreen video': {
                    height: 'auto',
                    width: 'auto',
                    maxHeight: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                },
            }}
        >
            {/* VIDEO */}
            <Box
                sx={{
                    position: 'relative',
                    height: embed ? '100%' : 'auto',
                    width: '100%',
                    aspectRatio: embed ? 'auto' : '16 / 9',
                    maxHeight: embed ? '100%' : '80vh',
                    display: 'flex',
                    justifyContent: 'center',
                    touchAction: 'pan-y',
                }}
            >
                <video
                    ref={videoRef}
                    poster={poster || undefined}
                    playsInline
                    preload="none"
                    style={{
                        width: '100%',
                        maxWidth: '100%',
                        height: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        background: '#000',
                    }}
                    onClick={onVideoClick}
                    onTouchStart={onVideoTouchStart}
                    onTouchMove={onVideoTouchMove}
                    onTouchEnd={onVideoTouchEnd}
                    onTouchCancel={onVideoTouchEnd}
                >
                    {src && <source src={src} type="application/vnd.apple.mpegurl" />}
                </video>
                {showStartCover && (
                    <Box
                        component="img"
                        src={poster}
                        alt={data.poster?.alt ?? data.title}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            background: '#000',
                            zIndex: 1,
                            pointerEvents: 'none',
                        }}
                    />
                )}

                {/* Loading / Buffering — idle before the source is attached, so no spinner yet */}
                {attached && (isBuffering || !isReady) && !seeking && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            pointerEvents: 'none',
                            bgcolor: 'rgba(0,0,0,0.15)',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!isTouchDevice && actionIcon && (
                    <Box
                        key={actionKey}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            pointerEvents: 'none',
                            zIndex: 5,
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 64, sm: 90 },
                                height: { xs: 64, sm: 90 },
                                borderRadius: '50%',
                                bgcolor: 'rgba(0,0,0,0.45)',
                                display: 'grid',
                                placeItems: 'center',
                                animation: `${actionIcon === 'play' ? 'playerActionPopIn' : 'playerActionPopOut'} 650ms ease-out`,
                                animationFillMode: 'forwards',
                                '& svg': {
                                    width: { xs: 26, sm: 36 },
                                    height: { xs: 26, sm: 36 },
                                },
                                '@keyframes playerActionPopOut': {
                                    '0%': { transform: 'scale(0.7)', opacity: 0 },
                                    '35%': { transform: 'scale(1)', opacity: 1 },
                                    '100%': { transform: 'scale(1.05)', opacity: 0 },
                                },
                                '@keyframes playerActionPopIn': {
                                    '0%': { transform: 'scale(1)', opacity: 1 },
                                    '100%': { transform: 'scale(0.65)', opacity: 0 },
                                },
                            }}
                            onAnimationEnd={() => setActionIcon(null)}
                        >
                            {actionIcon === 'play' ? (
                                <PlayIcon color="#fff" size={36} />
                            ) : (
                                <PauseIcon color="#fff" size={36} />
                            )}
                        </Box>
                    </Box>
                )}

                {!isTouchDevice && paused && !seeking && !actionIcon && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            zIndex: 3,
                            pointerEvents: 'none',
                        }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation()
                                togglePlay()
                            }}
                            sx={{
                                width: { xs: 64, sm: 90 },
                                height: { xs: 64, sm: 90 },
                                bgcolor: 'rgba(0,0,0,0.45)',
                                color: 'common.white',
                                pointerEvents: 'auto',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                            }}
                            aria-label="play video"
                        >
                            <PlayIcon color="#fff" size={36} />
                        </IconButton>
                    </Box>
                )}

                {isTouchDevice && controlsVisible && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            zIndex: 3,
                            pointerEvents: 'none',
                            transition: 'opacity 320ms ease',
                            opacity: mobileCenterActionShown ? 1 : 0,
                        }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation()
                                togglePlay()
                            }}
                            sx={{
                                width: 64,
                                height: 64,
                                bgcolor: 'rgba(0,0,0,0.45)',
                                color: 'common.white',
                                pointerEvents: mobileCenterActionShown ? 'auto' : 'none',
                                transition: 'opacity 320ms ease, transform 320ms ease',
                                opacity: mobileCenterActionShown ? 1 : 0,
                                transform: mobileCenterActionShown ? 'scale(1)' : 'scale(0.96)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                            }}
                            aria-label={paused ? 'play video' : 'pause video'}
                        >
                            {paused ? (
                                <PlayIcon color="#fff" size={30} />
                            ) : (
                                <PauseIcon color="#fff" size={30} />
                            )}
                        </IconButton>
                    </Box>
                )}


                {/* CONTROLS OVERLAY */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        px: { xs: 1, sm: 1.5 },
                        pb: { xs: 0, sm: 1.25 },
                        pt: { xs: 0.5, sm: 1 },
                        transition: 'opacity 150ms ease',
                        opacity: controlsVisible ? 1 : 0,
                        pointerEvents: controlsVisible ? 'auto' : 'none',
                        background:
                            'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.15), rgba(0,0,0,0))',
                    }}
                >
                    {/* Seek bar area with buffered indicator */}
                    {!showStartCover && (
                    <Box
                        sx={{
                            position: 'relative',
                            // position: { xs: 'absolute', sm: 'relative' },
                            // left: { xs: 0 },
                            // right: { xs: 0 },
                            // bottom: { xs: -3 },
                            // px: { xs: 0, sm: 1 },
                            pb: 0,
                        }}
                    >
                        <Slider
                            size="small"
                            min={0}
                            max={Math.max(0, duration || 0)}
                            step={0.05}
                            value={seeking ? seekValue : currentTime}
                            onMouseDown={() => {
                                if (!isTouchDevice) armBlockVideoClick()
                            }}
                            onTouchStart={() => {
                                if (!isTouchDevice) armBlockVideoClick()
                            }}
                            onPointerDown={() => {
                                if (!isTouchDevice) armBlockVideoClick()
                            }}
                            onChange={(_, val) => {
                                const v = Array.isArray(val) ? val[0] : val
                                const next = Number(v)
                                if (!isDraggingRef.current) {
                                    isDraggingRef.current = true
                                    setSeeking(true)
                                    setManualSeeking(true)
                                    manualSeekingRef.current = true
                                    onSeekingChange?.(true)
                                    showControls()
                                    const vid = videoRef.current
                                    if (vid && !vid.paused) {
                                        wasPlayingRef.current = true
                                        vid.pause()
                                    }
                                }
                                setSeekValue(next)
                                const vid = videoRef.current
                                if (vid) {
                                    vid.currentTime = clamp(next, 0, vid.duration || duration || 0)
                                    persistResume(vid.currentTime)
                                }
                            }}
                            onChangeCommitted={(_, val) => {
                                const v = Array.isArray(val) ? val[0] : val
                                seekTo(Number(v))
                                isDraggingRef.current = false
                                setSeeking(false)
                                setManualSeeking(false)
                                manualSeekingRef.current = false
                                showControls()
                                const vid = videoRef.current
                                if (vid && wasPlayingRef.current) {
                                    wasPlayingRef.current = false
                                    setResuming(true)
                                    const done = () => {
                                        setResuming(false)
                                        onSeekingChange?.(false)
                                    }
                                    vid.play().then(done, done)
                                } else {
                                    wasPlayingRef.current = false
                                    onSeekingChange?.(false)
                                }
                            }}
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                '--buffered-percent': `${bufferedPercent}%`,
                                py: {xs: 0, sm: 2},
                                color: 'common.white',
                                // pointerEvents: { xs: 'none', sm: 'auto' },
                                '& .MuiSlider-thumb': {
                                    width: { xs: 8, sm: 12 },
                                    height: { xs: 8, sm: 12 },
                                    boxShadow: 'none',
                                    transition: 'none',
                                    zIndex: 3,
                                    top: '50%',
                                    transform: 'translate(50%, -50%)',
                                },
                                '& .MuiSlider-thumb:before': { boxShadow: 'none' },
                                '& .MuiSlider-thumb:after': { width: 0, height: 0 },
                                '& .MuiSlider-rail': {
                                    position: 'relative',
                                    opacity: 1,
                                    height: { xs: 2, sm: 4 },
                                    borderRadius: 999,
                                    transition: 'none',
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 'var(--buffered-percent)',
                                        height: '100%',
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(255,255,255,0.6)',
                                        transition: 'width 160ms linear',
                                    },
                                },
                                '& .MuiSlider-track': {
                                    border: 0,
                                    height: { xs: 2, sm: 4 },
                                    borderRadius: 999,
                                    transition: 'none',
                                    zIndex: 2,
                                    backgroundColor: '#ff3b30',
                                },
                            }}
                        />
                    </Box>
                    )}

                    {/* Bottom row */}
                    <Stack
                        dir="ltr"
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: { xs: -0.25, sm: 0 }, mb: { xs: 0.75, sm: 0 }, px: { xs: 1, sm: 0 } }}
                    >
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Tooltip title={paused ? 'پخش (Space)' : 'توقف (Space)'}>
                                <IconButton onClick={togglePlay} sx={{ color: 'common.white', display: { xs: 'none', sm: 'inline-flex' } }}>
                                    {paused ? <PlayIcon color="#fff" size={20} /> : <PauseIcon color="#fff" size={20} />}
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="5 ثانیه عقب (←)">
                                <IconButton onClick={() => jump(-5)} sx={{ color: 'common.white', display: { xs: 'none', sm: 'inline-flex' } }}>
                                    <RotateCcwIcon color="#fff" size={20} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="5 ثانیه جلو (→)">
                                <IconButton onClick={() => jump(5)} sx={{ color: 'common.white', display: { xs: 'none', sm: 'inline-flex' } }}>
                                    <RotateCwIcon color="#fff" size={20} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={muted ? 'صدا وصل (M)' : 'بی‌صدا (M)'}>
                                <IconButton onClick={toggleMute} sx={{ color: 'common.white', display: { xs: 'none', sm: 'inline-flex' } }}>
                                    {muted || volume === 0 ? (
                                        <VolumeXIcon color="#fff" size={20} />
                                    ) : (
                                        <Volume2Icon color="#fff" size={20} />
                                    )}
                                </IconButton>
                            </Tooltip>

                            <CenterBox sx={{ width: 110, px: 1, pl: 2.5, display: { xs: 'none', sm: 'flex' } }}>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={muted ? 0 : volume}
                                    onChange={(_, val) => setPlayerVolume(Number(Array.isArray(val) ? val[0] : val))}
                                    sx={{
                                        color: 'common.white',
                                        '& .MuiSlider-thumb': { width: 10, height: 10 },
                                        '& .MuiSlider-rail': { opacity: 0.25 },
                                    }}
                                />
                            </CenterBox>

                            <Typography variant="caption" color="common.white" sx={{ opacity: 0.9, fontFeatureSettings: '"ss03"' }}>
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </Typography>
                        </Stack>

                        <Stack ref={menusRef} direction="row" alignItems="center" spacing={0.5}  >
                            {showQuality && (
                                <>
                                    <Box
                                        sx={{
                                            px: 0.75,
                                            py: 0.2,
                                            borderRadius: 0.75,
                                            bgcolor: 'rgba(0,0,0,0.55)',
                                            backdropFilter: 'blur(6px)',
                                            display: { xs: 'none', sm: 'inline-flex' },
                                        }}
                                    >
                                        <Typography variant="caption" color="common.white">
                                            {isAutoLevel ? 'خودکار' : (qualities[currentLevel]?.label ?? 'خودکار')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ position: 'relative' }}>
                                        <Tooltip title="کیفیت">
                                            <IconButton onClick={toggleQualityMenu} sx={{ color: 'common.white' }}>
                                                <SettingsIcon color="#fff" size={20} />
                                            </IconButton>
                                        </Tooltip>

                                        {qualityHover && (
                                            <Box
                                                dir="rtl"
                                                component="ul"
                                                sx={{
                                                    position: 'absolute',
                                                    left: '50%',
                                                    bottom: '100%',
                                                    transform: 'translateX(-50%)',
                                                    zIndex: { xs: 1, sm: 10 },
                                                    mb: 1,
                                                    minWidth: 130,
                                                    bgcolor: 'rgba(20,20,20,0.95)',
                                                    color: 'common.white',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    listStyle: 'none',
                                                    p: 0,
                                                    m: 0,
                                                    boxShadow: 8,
                                                }}
                                            >
                                                <Box
                                                    component="li"
                                                    onClick={() => onQualitySelect(-1)}
                                                    sx={{
                                                        px: 1,
                                                        py: 0.75,
                                                        cursor: 'pointer',
                                                        bgcolor: isAutoLevel ? 'rgba(255,255,255,0.12)' : 'transparent',
                                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                                    }}
                                                >
                                                    خودکار
                                                </Box>
                                                {qualities.map((q, i) => (
                                                    <Box
                                                        key={q.label}
                                                        component="li"
                                                        onClick={() => onQualitySelect(i)}
                                                        sx={{
                                                            px: 1,
                                                            py: 0.75,
                                                            cursor: 'pointer',
                                                            bgcolor: !isAutoLevel && currentLevel === i ? 'rgba(255,255,255,0.12)' : 'transparent',
                                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" justifyContent={'space-between'}>
                                                            <Typography>{q.label}</Typography>
                                                            {q.height >= 720 && (
                                                                <Box
                                                                    sx={{
                                                                        px: 0.4,
                                                                        py: 0.2,
                                                                        borderRadius: 0.5,
                                                                        fontSize: 11,
                                                                        border: '1px solid rgba(255,255,255,0.35)',
                                                                        color: 'common.white',
                                                                        fontFamily: 'monospace'
                                                                    }}
                                                                >
                                                                    {q.height >= 1080 ? "FHD" : "HD"}
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}

                            <Box sx={{ position: 'relative' }}>
                                <Tooltip title="سرعت پخش">
                                    <IconButton onClick={toggleSpeedMenu} sx={{ color: 'common.white' }}>
                                        <GaugeIcon color="#fff" size={20} />
                                    </IconButton>
                                </Tooltip>

                                {speedHover && (
                                    <Box
                                        dir="rtl"
                                        component="ul"
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            bottom: '100%',
                                            transform: 'translateX(-50%)',
                                            zIndex: { xs: 1, sm: 10 },
                                            mb: 1,
                                            minWidth: 110,
                                            bgcolor: 'rgba(20,20,20,0.95)',
                                            color: 'common.white',
                                            listStyle: 'none',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            p: 0,
                                            m: 0,
                                            boxShadow: 8,
                                        }}
                                    >
                                        {speedOptions.map((rate) => (
                                            <Box
                                                key={rate}
                                                component="li"
                                                onClick={() => onSpeedSelect(rate)}
                                                sx={{
                                                    px: 1,
                                                    py: 0.75,
                                                    cursor: 'pointer',
                                                    bgcolor: playbackRate === rate ? 'rgba(255,255,255,0.12)' : 'transparent',
                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                                }}
                                            >
                                                {rate}x
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {pipEnabled && (
                                <Tooltip title={isPip ? 'خروج از تصویر در تصویر' : 'تصویر در تصویر'}>
                                    <IconButton onClick={togglePip} sx={{ color: 'common.white' }}>
                                        {isPip ? (
                                            <PictureInPicture2Icon color="#fff" size={20} />
                                        ) : (
                                            <PictureInPictureIcon color="#fff" size={20} />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title={isFullscreen ? 'خروج از تمام‌صفحه' : 'تمام‌صفحه (F)'}>
                                <IconButton onClick={toggleFullscreen} sx={{ color: 'common.white' }}>
                                    {isFullscreen ? (
                                        <MinimizeIcon color="#fff" size={20} />
                                    ) : (
                                        <MaximizeIcon color="#fff" size={20} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    )
}
